import React, { useState, useEffect, useCallback } from 'react';
import './TrainingBooking.css';
import { trackAddToCart, trackTrainingBooked } from './analytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const CENTER_DATA = {
  'Lonsdale': { id: '68f9a3f8-4927-4808-b91a-68f71a47d771', name: 'Lonsdale', address: '111 3rd St West, North Vancouver' },
  'Mountain Hwy': { id: 'bb3035e6-32a2-451a-95ba-de4f281ceb8e', name: 'Mountain Hwy', address: '112-223 Mountain Hwy, North Vancouver' },
  'West Vancouver': { id: '099f4638-5eaa-4528-8730-54ee7b22555b', name: 'West Vancouver', address: '1570 Marine Drive, West Vancouver' }
};

// Block-out type ID for "Training" in Zenoti
const TRAINING_BLOCKOUT_TYPE_ID = 865;

// Toggle: set to true to require a credit card on file before booking
const REQUIRE_CARD = false;

function TrainingBooking() {
  // =====================
  // STEP FLOW:
  // 1 = Location
  // 2 = Date + Available Training Slots
  // 3 = Sign In / Sign Up
  // 4 = Confirm
  // 5 = Success
  // =====================

  // Popup auto-close flag (checked after hooks)
  const isCardPopup = typeof window !== 'undefined' && !!window.opener && window.location.search.includes('card_saved=1');

  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);

  // Location
  const [centers] = useState(Object.values(CENTER_DATA));
  const [selectedCenter, setSelectedCenter] = useState(null);

  // Training services (loaded once per center for name matching)
  const [trainingServices, setTrainingServices] = useState([]);

  // Date + Slots
  const [weekStart, setWeekStart] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [trainingSlots, setTrainingSlots] = useState([]); // parsed from block-outs
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [datesWithSlots, setDatesWithSlots] = useState({}); // { 'YYYY-MM-DD': true }

  // Auth state
  const [authMode, setAuthMode] = useState('phone');
  const [authPhone, setAuthPhone] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirmPassword, setAuthConfirmPassword] = useState('');
  const [authLoginUserName, setAuthLoginUserName] = useState('');
  const [authFirstName, setAuthFirstName] = useState('');
  const [authLastName, setAuthLastName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authDob, setAuthDob] = useState('');
  const [authGender, setAuthGender] = useState('');
  const [authReferralCode, setAuthReferralCode] = useState('');
  const [authHowHeard, setAuthHowHeard] = useState('');
  const [authMarketingEmail, setAuthMarketingEmail] = useState(true);
  const [authMarketingSms, setAuthMarketingSms] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [authOtp, setAuthOtp] = useState('');
  const [authVerificationId, setAuthVerificationId] = useState('');
  const [authUserName, setAuthUserName] = useState('');
  const [authIntent, setAuthIntent] = useState('otp');
  const [resetUserId, setResetUserId] = useState('');
  const [resetVerificationId, setResetVerificationId] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Booking
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingNotes, setBookingNotes] = useState('');
  const [unfilledForms, setUnfilledForms] = useState([]);

  // Card on file (for cancellation/no-show policy)
  const [cardCheckLoading, setCardCheckLoading] = useState(false);
  const [hasCardOnFile, setHasCardOnFile] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardIframeUrl, setCardIframeUrl] = useState(null);
  const [waitingForCard, setWaitingForCard] = useState(false);

  // Auto-advance toast (shown when we jump to first available day)
  const [showAutoAdvanceToast, setShowAutoAdvanceToast] = useState(false);
  const [autoAdvanceDate, setAutoAdvanceDate] = useState(null);

  // Reservation expiry timer (Step 4 confirm page)
  const [reservationSeconds, setReservationSeconds] = useState(null);

  // Policy agreement checkbox
  const [policyAgreed, setPolicyAgreed] = useState(false);

  // =====================
  // LOAD TRAINING SERVICES (for name matching)
  // =====================
  const loadTrainingServices = useCallback(async (centerId) => {
    try {
      const catRes = await fetch(`${API_URL}/api/training/categories/${centerId}`);
      const catData = await catRes.json();
      if (!catData.categoryId) return [];

      const svcRes = await fetch(`${API_URL}/api/training/services/${centerId}/${catData.categoryId}`);
      const svcData = await svcRes.json();
      const services = (svcData.services || []).filter(s => {
        if (s.add_ons_info?.is_add_on) return false;
        if (s.duration === 0 && (s.price_info?.sale_price || 0) === 0) return false;
        return true;
      });
      setTrainingServices(services);
      return services;
    } catch (err) {
      console.error('Failed to load training services:', err);
      return [];
    }
  }, []);

  // =====================
  // LOAD TRAINING BLOCK-OUTS FOR A DATE
  // =====================
  async function loadTrainingBlockOuts(centerId, date, servicesList) {
    setLoadingSlots(true);
    setTrainingSlots([]);
    setSelectedSlot(null);
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const res = await fetch(`${API_URL}/api/training/blockouts/${centerId}?date=${dateStr}`);
      const data = await res.json();
      
      const slots = (data.slots || []).map(slot => {
        // Try to match the notes to an actual training service
        const matchedService = matchServiceByNotes(slot.notes, servicesList);
        return {
          ...slot,
          matchedService,
          serviceName: matchedService ? (matchedService.display_name || matchedService.name) : null,
          serviceId: matchedService?.id || null,
          serviceDuration: matchedService?.duration || slot.durationMinutes,
          serviceDescription: matchedService?.html_description || matchedService?.description || null
        };
      }).filter(slot => slot.matchedService !== null); // ONLY show slots that match a real service

      // Sort by start time
      slots.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setTrainingSlots(slots);
    } catch (err) {
      console.error('Failed to load training block-outs:', err);
      setError('Failed to load training schedule.');
    } finally {
      setLoadingSlots(false);
    }
  }

  // Scan a week for dates that have training block-outs (for green dots)
  async function loadWeekAvailability(centerId, weekStartDate, servicesList) {
    try {
      const startStr = `${weekStartDate.getFullYear()}-${String(weekStartDate.getMonth() + 1).padStart(2, '0')}-${String(weekStartDate.getDate()).padStart(2, '0')}`;
      const endDate = new Date(weekStartDate);
      endDate.setDate(endDate.getDate() + 6);
      const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      const res = await fetch(`${API_URL}/api/training/blockouts/${centerId}?date=${startStr}&end_date=${endStr}`);
      const data = await res.json();
      
      // Only count dates where at least one slot matches a real service
      const matchedDates = {};
      (data.slots || []).forEach(slot => {
        const matched = matchServiceByNotes(slot.notes, servicesList);
        if (matched && slot.dateKey) matchedDates[slot.dateKey] = true;
      });
      
      setDatesWithSlots(prev => ({ ...prev, ...matchedDates }));
      return matchedDates;
    } catch (err) {
      console.error('Failed to scan week:', err);
      return {};
    }
  }

  function matchServiceByNotes(notes, servicesList) {
    const services = servicesList || trainingServices;
    if (!notes || !services.length) return null;
    const notesLower = notes.trim().toLowerCase();
    
    // Exact match only (case-insensitive)
    const match = services.find(s => {
      const name = (s.display_name || s.name || '').toLowerCase().trim();
      return name === notesLower;
    });
    return match || null;
  }

  // =====================
  // HANDLERS
  // =====================
  async function handleCenterSelect(center) {
    setSelectedCenter(center);
    setSelectedSlot(null);
    setSelectedSubCategory('all');
    setDatesWithSlots({});
    const services = await loadTrainingServices(center.id);
    
    const today = new Date();
    const sundayStart = new Date(today);
    sundayStart.setDate(sundayStart.getDate() - sundayStart.getDay());
    setWeekStart(sundayStart);
    setStep(2);
    
    // Scan this week + next 3 weeks to find first day with training
    setLoadingSlots(true);
    let foundDate = null;
    
    for (let weekOffset = 0; weekOffset < 4; weekOffset++) {
      const scanStart = new Date(today);
      scanStart.setDate(scanStart.getDate() + (weekOffset * 7));
      if (weekOffset > 0) scanStart.setHours(0,0,0,0);
      
      const dates = await loadWeekAvailability(center.id, scanStart, services);
      
      if (!foundDate) {
        const sortedDates = Object.keys(dates).sort();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const firstFuture = sortedDates.find(d => d >= todayStr);
        if (firstFuture) {
          foundDate = new Date(firstFuture + 'T12:00:00');
          const dayOfWeek = foundDate.getDay();
          const weekStartForDate = new Date(foundDate);
          weekStartForDate.setDate(weekStartForDate.getDate() - dayOfWeek);
          setWeekStart(weekStartForDate);
          setSelectedDate(foundDate);
          await loadTrainingBlockOuts(center.id, foundDate, services);
          // Show toast if we jumped past today
          if (firstFuture > todayStr) {
            setAutoAdvanceDate(foundDate);
            setShowAutoAdvanceToast(true);
            setTimeout(() => setShowAutoAdvanceToast(false), 5000);
          }
        }
      }
    }
    
    if (!foundDate) {
      setSelectedDate(today);
      setLoadingSlots(false);
    }
  }

  function handleDateSelect(date) {
    if (isPast(date) && !isToday(date)) return;
    setSelectedDate(date);
    setSelectedSlot(null);
    loadTrainingBlockOuts(selectedCenter.id, date);
  }

  function handleSlotSelect(slot) {
    setSelectedSlot(slot);
    trackAddToCart(slot.serviceName || 'Training', 0, 'Training');
  }

  async function handleSlotConfirm() {
    if (!selectedSlot) return;
    if (guestInfo) {
      // Already signed in — check card (if required) and go to confirm
      if (REQUIRE_CARD) {
        const hasCard = await checkGuestCard(guestInfo.guestId, guestInfo.webApiToken);
        setHasCardOnFile(hasCard);
      }
      setStep(4);
    } else {
      setStep(3); // Need to sign in
    }
  }

  // =====================
  // TIME/DATE HELPERS
  // =====================
  function getWeekDays(start) {
    const days = [];
    const d = new Date(start);
    for (let i = 0; i < 7; i++) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
    return days;
  }

  function formatDayName(date) { return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(); }
  function formatMonthYear(date) { return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase(); }
  function formatTime(timeStr) { return new Date(timeStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); }
  function isSameDay(d1, d2) { return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate(); }
  function isToday(date) { return isSameDay(date, new Date()); }
  function isPast(date) { const t = new Date(); t.setHours(0,0,0,0); const d = new Date(date); d.setHours(0,0,0,0); return d < t; }

  function handleWeekNav(direction) {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    const today = new Date(); today.setHours(0,0,0,0);
    // Block navigating to a week that ends before today (Saturday = Sunday + 6)
    const weekEnd = new Date(newStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    if (weekEnd < today) return;
    setWeekStart(newStart);
    setSelectedSlot(null);
    // Auto-select first day with training in new week, or first non-past day
    const firstDay = isPast(newStart) && !isToday(newStart) ? new Date() : newStart;
    setSelectedDate(firstDay);
    loadTrainingBlockOuts(selectedCenter.id, firstDay);
    loadWeekAvailability(selectedCenter.id, newStart);
  }

  // Get unique service names from today's slots for sub-category filtering
  function getSlotServiceNames() {
    const names = new Set();
    trainingSlots.forEach(slot => {
      if (slot.serviceName) names.add(slot.serviceName);
    });
    return Array.from(names);
  }

  function getFilteredSlots() {
    if (!selectedSubCategory || selectedSubCategory === 'all') return trainingSlots;
    return trainingSlots.filter(s => s.serviceName === selectedSubCategory);
  }

  // =====================
  // AUTH HANDLERS (same as before)
  // =====================
  function formatPhoneInput(value) {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  async function handlePhoneSubmit(intent) {
    const input = authPhone.trim();
    const digits = input.replace(/\D/g, '');
    const isPhone = digits.length >= 10 && /^\(?\d/.test(input);
    const isEmail = input.includes('@');
    // OTP is SMS-only — force password for email
    const chosenIntent = isEmail ? 'password' : (intent || 'otp');
    setAuthIntent(chosenIntent);
    if (!isPhone && !isEmail) { setAuthError('Please enter a valid phone number or email address.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const checkBody = isPhone ? { phone: digits } : { email: input };
      const checkRes = await fetch(`${API_URL}/api/auth/check`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(checkBody) });
      const checkData = await checkRes.json();
      if (checkData.exists) {
        const resolvedUserName = checkData.loginUserName || (isPhone ? digits : input);
        setAuthLoginUserName(resolvedUserName);
        if (checkData.multipleMatches && isPhone) {
          setAuthMode('multipleAccounts');
        } else if (chosenIntent === 'otp') {
          await sendOtpForUser(resolvedUserName);
        } else {
          setAuthMode('password');
        }
      } else {
        setAuthMode('signup');
      }
    } catch (err) { setAuthError('Something went wrong. Please try again.'); }
    finally { setAuthLoading(false); }
  }

  async function handleMultipleAccountsSubmit() {
    if (!authFirstName.trim()) { setAuthError('Please enter your first name.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const digits = authPhone.replace(/\D/g, '');
      const res = await fetch(`${API_URL}/api/auth/disambiguate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: digits, firstName: authFirstName.trim() }) });
      const data = await res.json();
      if (data.found && data.loginUserName) {
        setAuthLoginUserName(data.loginUserName);
        if (authIntent === 'otp') {
          await sendOtpForUser(data.loginUserName);
        } else {
          setAuthMode('password');
        }
      } else { setAuthError(data.error || 'Could not find your account.'); }
    } catch (err) { setAuthError('Something went wrong.'); }
    finally { setAuthLoading(false); }
  }

  async function sendOtpForUser(userName) {
    // OTP is SMS-only — if userName is an email, use the original phone number
    const otpTarget = userName.includes('@') ? authPhone.replace(/\D/g, '') : userName;
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: otpTarget, centerId: selectedCenter?.id })
      });
      const data = await res.json();
      if (data.success && data.verificationId) {
        setAuthVerificationId(data.verificationId);
        setAuthUserName(userName);
        setAuthOtp('');
        setAuthMode('otp');
      } else {
        setAuthError(data.error || 'Could not send verification code. Please try password instead.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    }
  }

  async function handleSendOtp() {
    const userName = authLoginUserName || authPhone.trim();
    if (!userName) { setAuthError('Please enter your phone or email first.'); return; }
    setAuthLoading(true); setAuthError('');
    await sendOtpForUser(userName);
    setAuthLoading(false);
  }

  async function handleOtpSubmit() {
    if (!authOtp || authOtp.length < 4) { setAuthError('Please enter the verification code.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: authUserName, otp: authOtp, verificationId: authVerificationId })
      });
      const data = await res.json();
      if (data.success) {
        const guest = { guestId: data.guestId, firstName: data.firstName, lastName: data.lastName, email: data.email, phone: data.phone, webApiToken: data.webApiToken };
        setGuestInfo(guest);
        if (REQUIRE_CARD) {
          const hasCard = await checkGuestCard(data.guestId, data.webApiToken);
          setHasCardOnFile(hasCard);
        }
        setStep(4);
      } else { setAuthError('Invalid code. Please try again.'); }
    } catch (err) { setAuthError('Something went wrong. Please try again.'); }
    finally { setAuthLoading(false); }
  }

  async function handlePasswordSubmit() {
    if (!authPhone || !authPassword) { setAuthError('Please enter your email/phone and password.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      let userName = authLoginUserName;
      if (!userName) {
        const input = authPhone.trim();
        const digits = input.replace(/\D/g, '');
        const isPhone = digits.length >= 10 && /^\(?\d/.test(input);
        const checkBody = isPhone ? { phone: digits } : { email: input };
        const checkRes = await fetch(`${API_URL}/api/auth/check`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(checkBody) });
        const checkData = await checkRes.json();
        if (checkData.exists && checkData.loginUserName) { userName = checkData.loginUserName; setAuthLoginUserName(userName); }
        else { userName = isPhone ? digits : input; }
      }
      const res = await fetch(`${API_URL}/api/auth/password-login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userName, password: authPassword }) });
      const data = await res.json();
      if (data.success) {
        const guest = { guestId: data.guestId, firstName: data.firstName, lastName: data.lastName, email: data.email, phone: data.phone, webApiToken: data.webApiToken };
        setGuestInfo(guest);
        // Check if guest has a card on file before proceeding (pass token directly since state is async)
        if (REQUIRE_CARD) {
          const hasCard = await checkGuestCard(data.guestId, data.webApiToken);
          setHasCardOnFile(hasCard);
        }
        setStep(4);
      } else { setAuthError(data.error || 'Incorrect password.'); }
    } catch (err) { setAuthError('Something went wrong.'); }
    finally { setAuthLoading(false); }
  }

  async function handleForgotPassword() {
    const userName = authLoginUserName || authPhone.trim();
    if (!userName) { setAuthError('Please enter your email or phone first.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userName }) });
      const data = await res.json();
      if (data.success) {
        setResetUserId(data.userId || '');
        setResetVerificationId(data.verificationId || '');
        setResetCode('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetSuccess(false);
        setAuthError('');
        setAuthMode('forgotSent');
      } else { setAuthError(data.error || 'Could not send reset email.'); }
    } catch (err) { setAuthError('Something went wrong.'); }
    finally { setAuthLoading(false); }
  }

  async function handleResetPassword() {
    if (!resetCode) { setAuthError('Please enter the code from your email.'); return; }
    if (!resetNewPassword || resetNewPassword.length < 8) { setAuthError('Password must be at least 8 characters.'); return; }
    if (resetNewPassword !== resetConfirmPassword) { setAuthError('Passwords do not match.'); return; }
    if (!/[a-z]/.test(resetNewPassword) || !/[A-Z]/.test(resetNewPassword) || !/[0-9]/.test(resetNewPassword) || !/[~!@#$%^&*()_+=]/.test(resetNewPassword)) {
      setAuthError('Password needs lowercase, uppercase, number, and special character.'); return;
    }
    if (!resetUserId) { setAuthError('Something went wrong. Please try resetting again.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: resetUserId, verificationId: resetVerificationId, code: resetCode, newPassword: resetNewPassword })
      });
      const data = await res.json();
      if (data.success) {
        setResetSuccess(true);
        setAuthError('');
      } else { setAuthError(data.error || 'Could not reset password. Please check your code and try again.'); }
    } catch (err) { setAuthError('Something went wrong.'); }
    finally { setAuthLoading(false); }
  }

  async function handleSignupSubmit() {
    if (!authFirstName || !authLastName || !authEmail || !authDob) { setAuthError('Please fill in all required fields.'); return; }
    if (!authPassword || authPassword.length < 8) { setAuthError('Password must be at least 8 characters.'); return; }
    if (authPassword !== authConfirmPassword) { setAuthError('Passwords do not match.'); return; }
    if (!/[a-z]/.test(authPassword) || !/[A-Z]/.test(authPassword) || !/[0-9]/.test(authPassword) || !/[~!@#$%^&*()_+=]/.test(authPassword)) {
      setAuthError('Password needs lowercase, uppercase, number, and special character.'); return;
    }
    const digits = authPhone.replace(/\D/g, '');
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/create-guest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: authFirstName, lastName: authLastName, phone: digits, email: authEmail,
          dateOfBirth: authDob, gender: authGender ? parseInt(authGender) : -1,
          referralCode: authReferralCode, howHeard: authHowHeard, password: authPassword,
          marketingEmail: authMarketingEmail, marketingSms: authMarketingSms, centerId: selectedCenter.id
        })
      });
      const data = await res.json();
      if (data.success) {
        const guest = { guestId: data.guestId, firstName: data.firstName, lastName: data.lastName, email: data.email, phone: data.phone, webApiToken: data.webApiToken };
        setGuestInfo(guest);
        // New accounts won't have a card, but check anyway for consistency
        if (REQUIRE_CARD) {
          const hasCard = await checkGuestCard(data.guestId, data.webApiToken);
          setHasCardOnFile(hasCard);
        }
        setStep(4);
      } else { setAuthError(data.error || 'Failed to create account.'); }
    } catch (err) { setAuthError('Something went wrong.'); }
    finally { setAuthLoading(false); }
  }

  // =====================
  // CARD ON FILE — Check + Save
  // =====================
  async function checkGuestCard(guestId, webApiToken) {
    setCardCheckLoading(true);
    try {
      const centerId = selectedCenter?.id || '';
      const token = webApiToken || guestInfo?.webApiToken || '';
      const res = await fetch(`${API_URL}/api/training/check-card/${guestId}?centerId=${centerId}&webApiToken=${encodeURIComponent(token)}`);
      const data = await res.json();
      // Filter out expired cards
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      const validCards = (data.cards || []).filter(c => {
        if (!c.expiryMonth || !c.expiryYear) return true; // no expiry info = assume valid
        if (c.expiryYear > currentYear) return true;
        if (c.expiryYear === currentYear && c.expiryMonth >= currentMonth) return true;
        return false;
      });
      const hasValid = validCards.length > 0;
      setHasCardOnFile(hasValid);
      setSavedCards(validCards);
      // Auto-select the first valid (or default) card
      if (hasValid) {
        const defaultCard = validCards.find(c => c.isDefault) || validCards[0];
        setSelectedCard(defaultCard);
      } else {
        setSelectedCard(null);
      }
      return hasValid;
    } catch (err) {
      console.error('Card check failed:', err);
      return false;
    } finally {
      setCardCheckLoading(false);
    }
  }

  async function handleSaveCard() {
    if (!guestInfo?.guestId || !selectedCenter?.id) return;
    setWaitingForCard(true);
    try {
      // Redirect URI goes to a simple "done" page — the popup will close itself
      const redirectUri = `${window.location.origin}/training?card_saved=1`;
      const res = await fetch(`${API_URL}/api/training/save-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: guestInfo.guestId,
          centerId: selectedCenter.id,
          redirectUri
        })
      });
      const data = await res.json();
      if (data.success && data.hostedPaymentUri) {
        // Open in a popup window for better autofill support
        const w = 500, h = 700;
        const left = (window.screen.width - w) / 2;
        const top = (window.screen.height - h) / 2;
        const popup = window.open(
          data.hostedPaymentUri,
          'AddCard',
          `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );
        if (!popup || popup.closed) {
          setError('Popup was blocked. Please allow popups for this site and try again.');
          setWaitingForCard(false);
          return;
        }
        // Watch for popup to close or redirect back to our site
        const pollPopup = setInterval(() => {
          try {
            // Check if popup navigated back to our site (card_saved=1)
            if (popup.location?.href?.includes('card_saved=1')) {
              popup.close();
              clearInterval(pollPopup);
              setWaitingForCard(false);
              setHasCardOnFile(true);
              setCardIframeUrl(null);
              return;
            }
          } catch (e) {
            // Cross-origin — popup is still on Zenoti/Adyen, keep waiting
          }
          if (popup.closed) {
            clearInterval(pollPopup);
            setCardIframeUrl(null);
            // Re-check with API whether card was actually saved
            if (guestInfo?.guestId) {
              checkGuestCard(guestInfo.guestId, guestInfo.webApiToken).finally(() => setWaitingForCard(false));
            } else {
              setWaitingForCard(false);
            }
          }
        }, 500);
      } else {
        setError(data.error || 'Could not open card entry form.');
        setWaitingForCard(false);
      }
    } catch (err) {
      console.error('Save card error:', err);
      setError('Something went wrong setting up card entry.');
      setWaitingForCard(false);
    }
  }

  // Listen for card save completion (if user navigates back in same window somehow)
  // If this is a popup returning from card entry, close it
  useEffect(() => {
    if (isCardPopup) {
      window.close();
    }
  }, [isCardPopup]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('card_saved') === '1' && !window.opener) {
      window.history.replaceState({}, '', '/training');
      setHasCardOnFile(true);
      setWaitingForCard(false);
      setCardIframeUrl(null);
    }
  }, []);

  // Reservation expiry timer — 5 minutes on the confirm page
  const RESERVATION_TIMEOUT = 5 * 60; // 5 minutes in seconds
  useEffect(() => {
    if (step === 4) {
      setReservationSeconds(RESERVATION_TIMEOUT);
      const interval = setInterval(() => {
        setReservationSeconds(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setReservationSeconds(null);
    }
  }, [step]);

  function formatCountdown(seconds) {
    if (seconds === null) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  // =====================
  // CONFIRM BOOKING
  // =====================
  async function handleConfirmBooking() {
    if (!guestInfo?.guestId || !selectedSlot) return;
    setBookingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId: selectedCenter.id,
          guestId: guestInfo.guestId,
          webApiToken: guestInfo.webApiToken,
          blockOutId: selectedSlot.blockOutId,
          employeeId: selectedSlot.therapistId,
          serviceId: selectedSlot.serviceId,
          serviceName: selectedSlot.serviceName,
          serviceDuration: selectedSlot.serviceDuration,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
          blockOutTypeId: selectedSlot.blockOutTypeId,
          notes: bookingNotes.trim()
        })
      });
      const data = await res.json();

      if (data.success) {
        trackTrainingBooked(selectedSlot.serviceName, selectedSlot.therapistName, selectedCenter?.name);
        setStep(5);
        if (data.unfilledForms?.length > 0) setUnfilledForms(data.unfilledForms);
      } else {
        setError(data.error || 'Failed to book training session. Please try again.');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  }

  // =====================
  // HELPERS
  // =====================
  function handleBack() {
    switch(step) {
      case 2: setStep(1); break;
      case 3: setStep(2); break;
      case 4: setStep(guestInfo ? 2 : 3); break;
      default: break;
    }
  }

  const Spinner = ({ text }) => (
    <div className="tb-loading">
      <div className="tb-spinner"></div>
      <span>{text || 'Loading...'}</span>
    </div>
  );

  // =====================
  // RENDER
  // =====================
  
  // If this is a popup from card entry, show a simple close message
  if (isCardPopup) {
    return <div style={{padding: '40px', textAlign: 'center', fontFamily: 'sans-serif'}}>✓ Card saved! This window will close automatically.</div>;
  }

  return (
    <div className="tb-container">
      {/* Header */}
      <header className="tb-header">
        <img src="/logo.png" alt="Cutie Pie Wax Bar" className="tb-logo" onError={(e) => e.target.style.display = 'none'} />
        <div className="tb-mode-toggle">
          <a href="/" className="tb-mode-tab">BOOK APPOINTMENT</a>
          <a href="/training" className="tb-mode-tab active">BOOK TRAINING</a>
        </div>
      </header>

      {/* Progress */}
      {step < 5 && (
        <div className="tb-progress">
          {[
            { label: 'Location', num: 1 },
            { label: 'Schedule', num: 2 },
            { label: 'Sign In', num: 3 },
            { label: 'Confirm', num: 4 },
          ].map((s, i, arr) => (
            <React.Fragment key={s.num}>
              <div
                className={`tb-progress-step ${step >= s.num ? 'active' : ''} ${step > s.num ? 'completed' : ''}`}
                onClick={() => step > s.num && setStep(s.num)}
                style={{ cursor: step > s.num ? 'pointer' : 'default' }}>
                <div className="tb-progress-circle">{step > s.num ? '✓' : s.num}</div>
                <div className="tb-progress-label">{s.label}</div>
              </div>
              {i < arr.length - 1 && <div className={`tb-progress-line ${step > s.num ? 'completed' : ''}`}></div>}
            </React.Fragment>
          ))}
        </div>
      )}

      <main className="tb-main">
        {error && (
          <div className="tb-error-banner">
            {error}
            <button onClick={() => setError(null)} className="tb-error-close">✕</button>
          </div>
        )}

        {step > 1 && step < 5 && (
          <button className="tb-back-btn" onClick={handleBack}>← Back</button>
        )}

        {/* ===================== STEP 1: LOCATION ===================== */}
        {step === 1 && (
          <div className="tb-step">
            <h1 className="tb-title">BOOK A TRAINING SESSION</h1>
            <p className="tb-subtitle">Choose your preferred location</p>
            <div className="tb-cards-grid">
              {centers.map(c => (
                <div key={c.id} className={`tb-card ${selectedCenter?.id === c.id ? 'selected' : ''}`} onClick={() => handleCenterSelect(c)}>
                  <div className="tb-card-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                  <h3>{c.name}</h3>
                  <p>{c.address}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===================== STEP 2: DATE + TRAINING SLOTS ===================== */}
        {step === 2 && (
          <div className="tb-step">
            <h1 className="tb-title">TRAINING SCHEDULE</h1>
            <p className="tb-subtitle">{selectedCenter?.name} — Pick a date to see available sessions</p>

            {/* Auto-advance toast */}
            {showAutoAdvanceToast && autoAdvanceDate && (
              <div className="tb-toast-mini">
                <span>Showing <strong>{autoAdvanceDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</strong> — next available day</span>
                <button className="tb-toast-mini-close" onClick={() => setShowAutoAdvanceToast(false)}>&times;</button>
              </div>
            )}

            {/* Week Calendar */}
            {weekStart && (
              <div className="tb-week-calendar">
                <div className="tb-week-header">
                  <h3>{formatMonthYear(weekStart)}</h3>
                  <div className="tb-week-nav">
                    <button onClick={() => handleWeekNav(-1)} disabled={(() => { const sat = new Date(weekStart); sat.setDate(sat.getDate() + 6); return isPast(sat); })()}>←</button>
                    <button onClick={() => handleWeekNav(1)}>→</button>
                  </div>
                </div>
                <div className="tb-week-days">
                  {getWeekDays(weekStart).map(day => {
                    const past = isPast(day) && !isToday(day);
                    const selected = selectedDate && isSameDay(day, selectedDate);
                    const dayStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                    const hasTraining = datesWithSlots[dayStr];
                    return (
                      <div key={day.toISOString()}
                        className={`tb-week-day ${selected ? 'selected' : ''} ${past ? 'disabled' : ''} ${isToday(day) ? 'today' : ''}`}
                        onClick={() => !past && handleDateSelect(day)}>
                        <span className="tb-day-name">{formatDayName(day)}</span>
                        <span className="tb-day-num">{day.getDate()}</span>
                        {!past && hasTraining && <span className="tb-avail-dot available"></span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Training Slots */}
            <div className="tb-training-slots">
              {loadingSlots ? <Spinner text="Loading training schedule..." /> : trainingSlots.length === 0 ? (
                <div className="tb-empty">
                  <p>Looks like training is all booked up for this day!</p>
                  <p className="tb-empty-hint">Try selecting a different date — days with a green dot have available sessions.</p>
                </div>
              ) : (() => {
                const serviceNames = getSlotServiceNames();
                const filteredSlots = getFilteredSlots();
                return (
                  <>
                    {serviceNames.length > 1 && (
                      <div className="tb-subcategory-pills">
                        <button className={`tb-subcategory-pill ${selectedSubCategory === 'all' ? 'active' : ''}`}
                          onClick={() => setSelectedSubCategory('all')}>All</button>
                        {serviceNames.map(name => (
                          <button key={name} className={`tb-subcategory-pill ${selectedSubCategory === name ? 'active' : ''}`}
                            onClick={() => setSelectedSubCategory(name)}>{name}</button>
                        ))}
                      </div>
                    )}
                    <div className="tb-slots-list">
                      {filteredSlots.map(slot => (
                        <div key={slot.blockOutId}
                          className={`tb-slot-card ${selectedSlot?.blockOutId === slot.blockOutId ? 'selected' : ''}`}
                          onClick={() => handleSlotSelect(slot)}>
                          <div className="tb-slot-time">
                            <span className="tb-slot-start">{formatTime(slot.startTime)}</span>
                            <span className="tb-slot-dash">–</span>
                            <span className="tb-slot-end">{formatTime(slot.endTime)}</span>
                          </div>
                          <div className="tb-slot-info">
                            <h4>{slot.serviceName}</h4>
                            {slot.serviceDuration > 0 && <p className="tb-slot-duration">{slot.serviceDuration} min</p>}
                            {slot.serviceDescription && slot.serviceDescription !== slot.serviceName && (
                              <div className="tb-slot-description" dangerouslySetInnerHTML={{ __html: slot.serviceDescription }} />
                            )}
                          </div>
                          <div className="tb-slot-check">
                            {selectedSlot?.blockOutId === slot.blockOutId && (
                              <svg viewBox="0 0 24 24" width="24" height="24" fill="#F78DA7" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="11"/><path d="M8 12l3 3 5-5" fill="none"/></svg>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ===================== STEP 3: SIGN IN ===================== */}
        {step === 3 && (
          <div className="tb-step">
            <h1 className="tb-title">
              {authMode === 'phone' ? 'SIGN IN TO BOOK' :
               authMode === 'multipleAccounts' ? 'MULTIPLE ACCOUNTS FOUND' :
               authMode === 'otp' ? 'ENTER VERIFICATION CODE' :
               authMode === 'password' ? 'LOG IN TO CONTINUE' :
               authMode === 'forgotSent' ? 'CHECK YOUR EMAIL' :
               'CREATE YOUR ACCOUNT'}
            </h1>
            <p className="tb-subtitle">{selectedCenter?.name}</p>

            <div className="tb-auth-container">
              {authMode === 'phone' && (
                <form className="tb-auth-form" onSubmit={(e) => { e.preventDefault(); handlePhoneSubmit(authPhone.includes('@') ? 'password' : 'otp'); }} autoComplete="on">
                  <p className="tb-auth-instruction">Enter your phone number or email to continue.</p>
                  <div className="tb-field">
                    <label>PHONE OR EMAIL</label>
                    <input type="text" placeholder="(604) 555-1234 or email@example.com"
                      autoComplete="username" name="username"
                      value={authPhone}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^[\d(]/.test(val) && !val.includes('@')) setAuthPhone(formatPhoneInput(val));
                        else setAuthPhone(val);
                      }} autoFocus />
                  </div>
                  {authError && <p className="tb-auth-error">{authError}</p>}
                  {!authPhone.includes('@') ? (
                    <>
                      <p className="tb-auth-hint">You'll receive a 6-digit verification code.</p>
                      <button type="submit" className="tb-btn-primary" disabled={authLoading}>
                        {authLoading ? 'CHECKING...' : 'SEND VERIFICATION CODE'}
                      </button>
                      <div className="tb-divider"><span>Or continue with</span></div>
                      <button type="button" className="tb-btn-secondary" onClick={() => handlePhoneSubmit('password')} disabled={authLoading}>
                        CONTINUE WITH PASSWORD
                      </button>
                    </>
                  ) : (
                    <button type="submit" className="tb-btn-primary" disabled={authLoading}>
                      {authLoading ? 'CHECKING...' : 'CONTINUE'}
                    </button>
                  )}
                  <div className="tb-divider"><span>New to Cutie Pie?</span></div>
                  <button type="button" className="tb-btn-secondary" onClick={() => { setAuthMode('signup'); setAuthError(''); }}>Create a new account</button>
                </form>
              )}

              {authMode === 'multipleAccounts' && (
                <div className="tb-auth-form">
                  <p className="tb-auth-instruction">We found more than one account with <strong>{authPhone}</strong>. Enter your first name to find yours.</p>
                  <div className="tb-field">
                    <label>FIRST NAME</label>
                    <input type="text" placeholder="Your first name" value={authFirstName}
                      onChange={(e) => setAuthFirstName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleMultipleAccountsSubmit()} autoFocus />
                  </div>
                  {authError && <p className="tb-auth-error">{authError}</p>}
                  <button className="tb-btn-primary" onClick={handleMultipleAccountsSubmit} disabled={authLoading}>
                    {authLoading ? 'LOOKING UP...' : 'CONTINUE'}
                  </button>
                  <button className="tb-btn-link" onClick={() => { setAuthMode('phone'); setAuthError(''); }}>Use a different number</button>
                </div>
              )}

              {authMode === 'otp' && (
                <div className="tb-auth-form">
                  <p className="tb-auth-instruction">We sent a verification code to <strong>{authPhone}</strong></p>
                  <div className="tb-field">
                    <label>VERIFICATION CODE</label>
                    <input type="text" placeholder="Enter 6-digit code"
                      value={authOtp}
                      onChange={(e) => setAuthOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={(e) => e.key === 'Enter' && handleOtpSubmit()}
                      autoFocus maxLength={6}
                      style={{ letterSpacing: '0.3em', fontSize: '1.2em', textAlign: 'center' }} />
                  </div>
                  {authError && <p className="tb-auth-error">{authError}</p>}
                  <button className="tb-btn-primary" onClick={handleOtpSubmit} disabled={authLoading}>
                    {authLoading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
                  </button>
                  <button type="button" className="tb-btn-link" onClick={() => { setAuthError(''); handleSendOtp(); }} disabled={authLoading}>Resend code</button>
                  <div className="tb-divider"><span>Or continue with</span></div>
                  <button type="button" className="tb-btn-secondary" onClick={() => { setAuthMode('password'); setAuthError(''); setAuthPassword(''); }}>
                    CONTINUE WITH PASSWORD
                  </button>
                  <button type="button" className="tb-btn-link" onClick={() => { setAuthMode('phone'); setAuthOtp(''); setAuthError(''); }}>Use a different number</button>
                </div>
              )}

              {authMode === 'password' && (
                <form className="tb-auth-form" onSubmit={(e) => { e.preventDefault(); handlePasswordSubmit(); }} autoComplete="on">
                  {authLoginUserName ? (
                    <>
                      <p className="tb-auth-instruction">Welcome back{authFirstName ? `, ${authFirstName}` : ''}! Enter your password.</p>
                      {/* Hidden username field so browser knows which account */}
                      <input type="hidden" name="username" autoComplete="username" value={authLoginUserName} />
                      <div className="tb-field">
                        <label>PASSWORD</label>
                        <div className="tb-password-wrapper">
                          <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                            autoComplete="current-password" name="password"
                            value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} autoFocus />
                          <button type="button" className="tb-show-pw" onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide' : 'Show'}</button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="tb-auth-instruction">Welcome back! Sign in with your email or phone.</p>
                      <div className="tb-field">
                        <label>EMAIL OR PHONE</label>
                        <input type="text" placeholder="Email or phone number" autoComplete="username" name="username" value={authPhone}
                          onChange={(e) => { setAuthPhone(e.target.value); setAuthLoginUserName(''); }} autoFocus />
                      </div>
                      <div className="tb-field">
                        <label>PASSWORD</label>
                        <div className="tb-password-wrapper">
                          <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                            autoComplete="current-password" name="password"
                            value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} />
                          <button type="button" className="tb-show-pw" onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide' : 'Show'}</button>
                        </div>
                      </div>
                    </>
                  )}
                  {authError && <p className="tb-auth-error">{authError}</p>}
                  <button type="submit" className="tb-btn-primary" disabled={authLoading}>
                    {authLoading ? 'SIGNING IN...' : 'LOG IN'}
                  </button>
                  <button type="button" className="tb-btn-link" onClick={handleForgotPassword} disabled={authLoading}>Forgot your password?</button>
                  <button type="button" className="tb-btn-link" onClick={handleSendOtp} disabled={authLoading}>Use verification code instead</button>
                  <button type="button" className="tb-btn-link" onClick={() => { setAuthMode('phone'); setAuthError(''); setAuthPassword(''); }}>Use a different number</button>
                  <div className="tb-divider"><span>Don't have an account?</span></div>
                  <button type="button" className="tb-btn-secondary" onClick={() => { setAuthMode('signup'); setAuthError(''); }}>Create a new account</button>
                </form>
              )}

              {authMode === 'forgotSent' && (
                <div className="tb-auth-form">
                  {resetSuccess ? (
                    <>
                      <p className="tb-auth-instruction">Your password has been reset successfully!</p>
                      <button className="tb-btn-primary" onClick={() => { setAuthMode('password'); setAuthError(''); setAuthPassword(''); }}>LOG IN WITH NEW PASSWORD</button>
                    </>
                  ) : (
                    <>
                      <p className="tb-auth-instruction">We've sent a reset code to your email. Enter it below with your new password.</p>
                      <div className="tb-field">
                        <label>RESET CODE</label>
                        <input type="text" placeholder="Enter code from email" value={resetCode}
                          onChange={(e) => setResetCode(e.target.value)} autoFocus />
                      </div>
                      <div className="tb-field">
                        <label>NEW PASSWORD</label>
                        <div className="tb-password-wrapper">
                          <input type={showPassword ? 'text' : 'password'} placeholder="New password"
                            autoComplete="new-password" value={resetNewPassword}
                            onChange={(e) => setResetNewPassword(e.target.value)} />
                          <button type="button" className="tb-show-pw" onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide' : 'Show'}</button>
                        </div>
                      </div>
                      <div className="tb-field">
                        <label>CONFIRM PASSWORD</label>
                        <input type="password" placeholder="Confirm new password"
                          autoComplete="new-password" value={resetConfirmPassword}
                          onChange={(e) => setResetConfirmPassword(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()} />
                      </div>
                      <div className="tb-password-rules">
                        <p>Password must contain:</p>
                        <span className={resetNewPassword.length >= 8 ? 'pass' : ''}>8+ characters</span>
                        <span className={/[a-z]/.test(resetNewPassword) ? 'pass' : ''}>Lowercase</span>
                        <span className={/[A-Z]/.test(resetNewPassword) ? 'pass' : ''}>Uppercase</span>
                        <span className={/[0-9]/.test(resetNewPassword) ? 'pass' : ''}>Number</span>
                        <span className={/[~!@#$%^&*()_+=]/.test(resetNewPassword) ? 'pass' : ''}>Special character</span>
                      </div>
                      {authError && <p className="tb-auth-error">{authError}</p>}
                      <button className="tb-btn-primary" onClick={handleResetPassword} disabled={authLoading}>
                        {authLoading ? 'RESETTING...' : 'RESET PASSWORD'}
                      </button>
                      <button className="tb-btn-link" onClick={handleForgotPassword} disabled={authLoading}>Resend code</button>
                      <button className="tb-btn-link" onClick={() => { setAuthMode('password'); setAuthError(''); }}>Back to log in</button>
                    </>
                  )}
                </div>
              )}

              {authMode === 'signup' && (
                <div className="tb-auth-form">
                  <p className="tb-auth-instruction">We don't have an account for <strong>{authPhone}</strong>. Let's create one!</p>
                  <div className="tb-field-row">
                    <div className="tb-field"><label>FIRST NAME *</label><input type="text" placeholder="First name" value={authFirstName} onChange={(e) => setAuthFirstName(e.target.value)} autoFocus /></div>
                    <div className="tb-field"><label>LAST NAME *</label><input type="text" placeholder="Last name" value={authLastName} onChange={(e) => setAuthLastName(e.target.value)} /></div>
                  </div>
                  <div className="tb-field-row">
                    <div className="tb-field"><label>DATE OF BIRTH *</label><input type="date" value={authDob} onChange={(e) => setAuthDob(e.target.value)} /></div>
                    <div className="tb-field"><label>GENDER</label>
                      <select value={authGender} onChange={(e) => setAuthGender(e.target.value)}>
                        <option value="">Prefer not to say</option><option value="1">Female</option><option value="0">Male</option><option value="2">Non-binary</option>
                      </select>
                    </div>
                  </div>
                  <div className="tb-field-row">
                    <div className="tb-field"><label>EMAIL *</label><input type="email" placeholder="email@example.com" autoComplete="email" name="email" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} /></div>
                    <div className="tb-field"><label>PHONE</label><input type="text" value={authPhone} onChange={(e) => setAuthPhone(formatPhoneInput(e.target.value))} placeholder="(778) 555-1234" /></div>
                  </div>
                  <div className="tb-field-row">
                    <div className="tb-field"><label>REFERRAL CODE</label><input type="text" placeholder="Optional" value={authReferralCode} onChange={(e) => setAuthReferralCode(e.target.value)} /></div>
                    <div className="tb-field"><label>HOW DID YOU HEAR ABOUT US?</label>
                      <select value={authHowHeard} onChange={(e) => setAuthHowHeard(e.target.value)}>
                        <option value="">Select...</option>
                        <option value="Advertisement">Advertisement</option><option value="Chat Gpt">Chat GPT</option><option value="Facebook">Facebook</option>
                        <option value="Friend Referral">Friend Referral</option><option value="Instagram">Instagram</option><option value="Internet Search">Internet Search</option>
                        <option value="Other">Other</option><option value="Walk-in">Walk-in</option><option value="Word of Mouth">Word of Mouth</option>
                      </select>
                    </div>
                  </div>
                  <div className="tb-field-row">
                    <div className="tb-field"><label>PASSWORD *</label><input type="password" placeholder="Password" autoComplete="new-password" name="new-password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} /></div>
                    <div className="tb-field"><label>CONFIRM PASSWORD *</label><input type="password" placeholder="Confirm" autoComplete="new-password" name="confirm-password" value={authConfirmPassword} onChange={(e) => setAuthConfirmPassword(e.target.value)} /></div>
                  </div>
                  <div className="tb-password-rules">
                    <p>Password must contain:</p>
                    <span className={authPassword.length >= 8 ? 'pass' : ''}>8+ characters</span>
                    <span className={/[a-z]/.test(authPassword) ? 'pass' : ''}>Lowercase</span>
                    <span className={/[A-Z]/.test(authPassword) ? 'pass' : ''}>Uppercase</span>
                    <span className={/[0-9]/.test(authPassword) ? 'pass' : ''}>Number</span>
                    <span className={/[~!@#$%^&*()_+=]/.test(authPassword) ? 'pass' : ''}>Special character</span>
                  </div>
                  <div className="tb-marketing">
                    <p>Marketing Opt-in</p>
                    <label><input type="checkbox" checked={authMarketingEmail} onChange={(e) => setAuthMarketingEmail(e.target.checked)} /> Email</label>
                    <label><input type="checkbox" checked={authMarketingSms} onChange={(e) => setAuthMarketingSms(e.target.checked)} /> SMS</label>
                  </div>
                  {authError && <p className="tb-auth-error">{authError}</p>}
                  <button className="tb-btn-primary" onClick={handleSignupSubmit} disabled={authLoading}>
                    {authLoading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT & BOOK'}
                  </button>
                  <button className="tb-btn-link" onClick={() => { setAuthMode('phone'); setAuthError(''); }}>Use a different number</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===================== STEP 4: CONFIRM ===================== */}
        {step === 4 && selectedSlot && (
          <div className="tb-step">
            {/* Reservation timer */}
            {reservationSeconds !== null && reservationSeconds > 0 && (
              <div className={`tb-reservation-timer ${reservationSeconds <= 60 ? 'urgent' : ''}`}>
                Your reservation will expire in {formatCountdown(reservationSeconds)}
              </div>
            )}

            {/* Reservation expired modal */}
            {reservationSeconds === 0 && (
              <div className="tb-modal-overlay">
                <div className="tb-modal">
                  <h3>YOUR RESERVATION HAS EXPIRED</h3>
                  <p>Choose your date and time and reserve again.</p>
                  <button className="tb-btn-primary" onClick={() => { setStep(2); setSelectedSlot(null); }}>OK</button>
                </div>
              </div>
            )}

            <div className="tb-confirm-layout">
              {/* LEFT COLUMN — Booking details */}
              <div className="tb-confirm-left">
                <div className="tb-confirm-section">
                  <div className="tb-confirm-field">
                    <div className="tb-confirm-field-header">
                      <strong>Date</strong>
                      <button className="tb-edit-link" onClick={() => setStep(2)}>Edit</button>
                    </div>
                    <p>{selectedDate && selectedDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    <p className="tb-confirm-detail">{formatTime(selectedSlot.startTime)} – {formatTime(selectedSlot.endTime)}</p>
                  </div>
                </div>

                <div className="tb-confirm-section">
                  <div className="tb-confirm-field">
                    <div className="tb-confirm-field-header">
                      <strong>Location</strong>
                      <button className="tb-edit-link" onClick={() => setStep(1)}>Edit</button>
                    </div>
                    <p>{selectedCenter?.name}</p>
                    <p className="tb-confirm-detail">{selectedCenter?.address}</p>
                  </div>
                </div>

                {guestInfo && (
                  <div className="tb-confirm-section">
                    <div className="tb-confirm-field">
                      <strong>Guest</strong>
                      <p>{guestInfo.firstName} {guestInfo.lastName}</p>
                    </div>
                  </div>
                )}

                <div className="tb-confirm-section">
                  <div className="tb-confirm-field">
                    <div className="tb-confirm-field-header">
                      <strong>Service</strong>
                    </div>
                    <p>{selectedSlot.serviceName}</p>
                    {selectedSlot.serviceDuration > 0 && <p className="tb-confirm-detail">{selectedSlot.serviceDuration} mins</p>}
                  </div>
                </div>

              </div>

              {/* RIGHT COLUMN — Card, total, policy */}
              <div className="tb-confirm-right">
                <div className="tb-confirm-total-section">
                  <div className="tb-confirm-total-row">
                    <span>Total</span>
                    <span className="tb-confirm-total-price">Free</span>
                  </div>
                </div>

                <div className="tb-confirm-section">
                  <div className="tb-confirm-field">
                    <label>Notes or preferences</label>
                    <textarea placeholder="Add any notes for your trainer..." value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)} rows={2} className="tb-confirm-textarea" />
                  </div>
                </div>

                {/* Card on file section — only shown when REQUIRE_CARD is true */}
                {REQUIRE_CARD && (
                <div className="tb-confirm-card-section">
                  {cardCheckLoading ? (
                    <div className="tb-card-status loading">
                      <span className="tb-spinner-small"></span>
                      <span>Checking payment method...</span>
                    </div>
                  ) : hasCardOnFile && selectedCard ? (
                    <>
                      <div className="tb-card-secured">
                        <span className="tb-card-secured-check">&#10003;</span>
                        <span>Appointment secured with card ending in {selectedCard.lastFour}</span>
                      </div>
                      {cardIframeUrl ? (
                        <div className="tb-card-waiting-wrapper">
                          <div className="tb-card-waiting">
                            <span className="tb-spinner-small"></span>
                            <span>Complete card entry in the popup window...</span>
                          </div>
                          <button className="tb-card-retry-link" onClick={() => { setCardIframeUrl(null); setWaitingForCard(false); }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="tb-add-card-btn" onClick={handleSaveCard} disabled={waitingForCard} style={{ marginTop: '10px' }}>
                          <span className="tb-add-card-icon">+</span>
                          <span>{waitingForCard ? 'Opening...' : 'Add a new card'}</span>
                          <span className="tb-add-card-arrow">&rsaquo;</span>
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <h4>Secure your booking</h4>
                      <p className="tb-confirm-card-note">A card on file is required. Your card will not be charged.</p>
                      {cardIframeUrl ? (
                        <div className="tb-card-waiting-wrapper">
                          <div className="tb-card-waiting">
                            <span className="tb-spinner-small"></span>
                            <span>Complete card entry in the popup window...</span>
                          </div>
                          <button className="tb-card-retry-link" onClick={() => { setCardIframeUrl(null); setWaitingForCard(false); }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="tb-add-card-btn" onClick={handleSaveCard} disabled={waitingForCard}>
                          <span className="tb-add-card-icon">+</span>
                          <span>{waitingForCard ? 'Opening...' : 'Add a new card'}</span>
                          <span className="tb-add-card-arrow">&rsaquo;</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
                )}

                {/* Policy always required */}
                <div className="tb-confirm-policy">
                  <label className="tb-policy-checkbox">
                    <input type="checkbox" checked={policyAgreed} onChange={(e) => setPolicyAgreed(e.target.checked)} />
                    <span>{REQUIRE_CARD
                      ? 'I agree to the cancellation policy. My card will not be charged. I am free to cancel or change my session up to 12 hours before with no charge. Cancellations with less than 12 hours notice and no-shows are charged $50 using the card on file.'
                      : 'I agree to the cancellation policy. I am free to cancel or change my session up to 12 hours before with no charge. Cancellations with less than 12 hours notice and no-shows may be charged $50.'
                    }</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================== STEP 5: SUCCESS ===================== */}
        {step === 5 && (
          <div className="tb-step">
            <div className="tb-success">
              <div className="tb-success-icon">✓</div>
              <h2>Training Session Booked!</h2>
              <p className="tb-success-subtitle">A confirmation has been sent to {guestInfo?.email || 'your email'}.</p>

              <div className="tb-success-details">
                <div className="tb-summary-row"><span>Service:</span><span>{selectedSlot?.serviceName}</span></div>
                <div className="tb-summary-row"><span>Location:</span><span>{selectedCenter?.name}</span></div>
                {selectedDate && (
                  <div className="tb-summary-row">
                    <span>Date:</span>
                    <span>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                  </div>
                )}
                {selectedSlot && <div className="tb-summary-row"><span>Time:</span><span>{formatTime(selectedSlot.startTime)} – {formatTime(selectedSlot.endTime)}</span></div>}
              </div>

              {unfilledForms.length > 0 && (
                <div className="tb-forms-notice">
                  <h4>Please complete these forms before your session:</h4>
                  {unfilledForms.map((form, i) => (
                    <a key={i} href={form.formUrl} target="_blank" rel="noopener noreferrer" className="tb-form-link">{form.name} →</a>
                  ))}
                </div>
              )}

              {/* Add to calendar */}
              <div className="tb-calendar-links">
                <p>Add to Calendar:</p>
                <div className="tb-calendar-btns">
                  <a className="tb-calendar-btn" href={(() => {
                    if (!selectedSlot) return '#';
                    const start = new Date(selectedSlot.startTime);
                    const end = new Date(selectedSlot.endTime);
                    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
                    const title = encodeURIComponent(`Training - Cutie Pie Wax Bar ${selectedCenter?.name}`);
                    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}`;
                  })()} target="_blank" rel="noopener noreferrer">Google</a>
                  <a className="tb-calendar-btn" href={(() => {
                    if (!selectedSlot) return '#';
                    const start = new Date(selectedSlot.startTime);
                    const end = new Date(selectedSlot.endTime);
                    const title = encodeURIComponent(`Training - Cutie Pie Wax Bar ${selectedCenter?.name}`);
                    return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${start.toISOString()}&enddt=${end.toISOString()}`;
                  })()} target="_blank" rel="noopener noreferrer">Outlook</a>
                  <button className="tb-calendar-btn" onClick={() => {
                    if (!selectedSlot) return;
                    const start = new Date(selectedSlot.startTime);
                    const end = new Date(selectedSlot.endTime);
                    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
                    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nSUMMARY:Training - Cutie Pie Wax Bar ${selectedCenter?.name}\nEND:VEVENT\nEND:VCALENDAR`;
                    const blob = new Blob([ics], { type: 'text/calendar' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'training.ics'; a.click();
                  }}>Apple</button>
                </div>
              </div>

              <button className="tb-btn-primary" onClick={() => window.location.reload()} style={{ marginTop: '30px' }}>
                BOOK ANOTHER SESSION
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Sticky footer button */}
      {step === 2 && selectedSlot && (
        <div className="tb-sticky-footer">
          <button className="tb-btn-primary" onClick={handleSlotConfirm}>
            BOOK — {selectedSlot.serviceName} at {formatTime(selectedSlot.startTime)}
          </button>
        </div>
      )}
      {step === 4 && (
        <div className="tb-sticky-footer">
          <button className="tb-btn-primary" onClick={handleConfirmBooking} disabled={bookingLoading || !policyAgreed}>
            {bookingLoading ? 'BOOKING...' : 'CONFIRM TRAINING SESSION'}
          </button>
        </div>
      )}
    </div>
  );
}

export default TrainingBooking;
